import { memo, useState, useCallback, useMemo } from 'react'
import { textPrimary } from 'styles/Colors'
import Typography from 'components/common/Typography'
import DefaultBtn from 'components/common/buttons/Default/Component'
import DefaultCross from 'assets/icons/crosses/Default'
import LoaderView from 'components/common/loaders/LoaderView'
import { useTranslation } from 'services/Translation'
import { useTimeOffDispatch, create as createTimeOff, deleteTimeOff } from 'services/TimeOff'
import { getAccessTokenAndClaims } from 'services/Authentication'
import { Container } from './Styles'
import BlockView from './BlockView'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    blockViewTitle: getText('Time slot customization', 'Schedule_Sidebar_Event_Manager'),
    patientDetails: getText('Patient details', 'Schedule_Sidebar_Event_Manager'),
    closeBtnLabel: getText('Close', 'Schedule_Sidebar_Event_Manager'),
    customizeLabelBtn: getText('Customize time slot(s)', 'Schedule_Sidebar_Event_Manager'),
    audioVisual: getText('Video consult', 'Schedule_Sidebar_Event_Manager'),
    reschedule: getText('Reschedule', 'Schedule_Sidebar_Event_Manager'),
    empty: getText('Currently the slot is empty', 'Schedule_Sidebar_Event_Manager'),
    block: getText('Block timeslot', 'Schedule_Sidebar_Event_Manager'),
    unblock: getText('Unblock timeslot', 'Schedule_Sidebar_Event_Manager'),
    remove: getText('Remove timeslot', 'Schedule_Sidebar_Event_Manager'),
    blockingTimeslots: getText('Blocking timeslots...', 'Schedule_Sidebar_Event_Manager'),
    unblockingTimeslots: getText('Unblocking timeslots...', 'Schedule_Sidebar_Event_Manager'),
    pleaseWait: getText('Please wait', 'Schedule_Sidebar_Event_Manager'),
    selectedTimeslots: getText('Selected timeslots:', 'Schedule_Sidebar_Event_Manager'),
  }[key])

/**
 * Groups consecutive timeslots into ranges and formats them.
 * For example: [7:00, 7:10, 7:20, 8:50, 9:00] becomes "7:00 - 7:30, 8:50 - 9:10"
 */
function buildTimeslotsRangeText(slots: any[], formatDate: (date: Date, format: string) => string) {
  if (slots.length === 0) return ''

  // Sort slots by time
  const sortedSlots = [...slots].sort((a, b) => 
    new Date(a.time).getTime() - new Date(b.time).getTime()
  )

  // Group consecutive slots into ranges
  // Slots are consecutive if they're 10 minutes apart
  const ranges: { start: Date; end: Date }[] = []
  let currentRange: { start: Date; end: Date } | null = null

  sortedSlots.forEach((slot) => {
    const slotTime = new Date(slot.time)
    // Each slot is 10 minutes, so end time is start + 10 minutes
    const slotEndTime = new Date(slotTime.getTime() + 10 * 60 * 1000)

    if (!currentRange) {
      // Start a new range
      currentRange = { start: slotTime, end: slotEndTime }
    } else {
      // Check if this slot is consecutive (within 10 minutes of current range end)
      const timeDiff = slotTime.getTime() - currentRange.end.getTime()
      
      if (timeDiff <= 0) {
        // Slot overlaps or is at the end of current range - extend it
        currentRange.end = slotEndTime
      } else if (timeDiff <= 10 * 60 * 1000) {
        // Slot is consecutive (10 minutes gap or less) - extend range
        currentRange.end = slotEndTime
      } else {
        // Gap detected - save current range and start new one
        ranges.push(currentRange)
        currentRange = { start: slotTime, end: slotEndTime }
      }
    }
  })

  // Don't forget the last range
  if (currentRange) {
    ranges.push(currentRange)
  }

  // Format ranges as "start - end"
  return ranges
    .map((range) => {
      const startStr = formatDate(range.start, 'h:mm a')
      const endStr = formatDate(range.end, 'h:mm a')
      return `${startStr} - ${endStr}`
    })
    .join(', ')
}

const contentVariants = {
  initial: {
    opacity: 0,
    x: '100%',
    transition: {
      when: 'afterChildren',
    },
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      when: 'beforeChildren',
    },
  },
}

interface EventManagerProps {
  onClose: () => void
  timeSlots: any[]
}

const EventManager = ({ onClose, timeSlots }: EventManagerProps) => {
  const { t, formatDate } = useTranslation(texts)
  const [showBlockView, setShowBlockView] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const timeOffDispatch = useTimeOffDispatch()

  const { token, claims } = getAccessTokenAndClaims()
  const staffId = claims?.sub

  const isBlocked = useMemo(() => {
    return !!timeSlots?.[0]?.doctorId
  }, [timeSlots])

  const timeslotsRangeText = useMemo(() => {
    return buildTimeslotsRangeText(timeSlots, formatDate)
  }, [timeSlots, formatDate])

  const handleShowBlockView = useCallback(() => {
    setShowBlockView(v => !v)
  }, [])

  const handleCreate = useCallback(
    async (newBlockedTime: any) => {
      setIsProcessing(true)
      
      try {
        await createTimeOff(
          {
            token,
          },
          { data: newBlockedTime, staffId },
          timeOffDispatch,
          true
        )
        
        // Add a small delay to ensure the UI updates properly
        await new Promise(resolve => setTimeout(resolve, 1000))
      } catch (error) {
        console.error('Error blocking timeslot:', error)
      } finally {
        setIsProcessing(false)
      }
    },
    [token, staffId, timeOffDispatch, t]
  )

  const handleDelete = useCallback(async () => {
    setIsProcessing(true)
    
    try {
      await Promise.all(
        timeSlots.map(({ id }) => {
          return deleteTimeOff({ token }, { timeOffId: id, staffId }, timeOffDispatch, true)
        })
      )
      
      // Add a small delay to ensure the UI updates properly
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      onClose()
    } catch (error) {
      console.error('Error unblocking timeslot:', error)
    } finally {
      setIsProcessing(false)
    }
  }, [onClose, token, staffId, timeOffDispatch, timeSlots, t])

  // Show loading modal during processing
  if (isProcessing) {
    return (
      <LoaderView 
        loading
        onClose={() => {}} // Prevent closing during processing
      />
    )
  }

  return (
    <>
      <Container initial="initial" animate="visible" variants={contentVariants}>
        <DefaultBtn
          mini
          label={t('closeBtnLabel')}
          onClick={onClose}
          className="absolute top-9 left-0 z-10"
          style={{ transform: 'translate(-50%, -50%)' }}
          nextIcon={<DefaultCross style={{ width: 12, height: 'auto' }} />}
          background={textPrimary}
          color="white"
        />
        {showBlockView ? (
          <BlockView
            timeSlots={timeSlots}
            onShowBlockView={handleShowBlockView}
            createTimeoff={handleCreate}
            onClose={onClose}
          />
        ) : (
          <>
            <div className="px-8 pt-4">
              <div className="h-12 w-full flex items-center justify-start relative z-30 border-b border-grey-100">
                <Typography
                  size={18}
                  className="font-csc65 pb-3"
                  text={t('blockViewTitle')}
                  style={undefined}
                />
              </div>
            </div>
            
            {/* Timeslot Info */}
            <div className="px-8 py-4">
              <Typography
                size={14}
                className="font-csc65 text-gray-700 mb-2"
                text={t('selectedTimeslots')}
                style={undefined}
              />
              <Typography
                size={14}
                className="font-csc45 text-gray-600"
                text={timeslotsRangeText}
                style={undefined}
              />
            </div>

            {/* Action Buttons */}
            <div className="px-8 pb-8 space-y-3">
              {isBlocked ? (
                <DefaultBtn
                  label={t('unblock')}
                  onClick={handleDelete}
                  disabled={isProcessing}
                  className="font-csc65 w-full"
                  style={{ backgroundColor: '#9B2A3B' }}
                  color="white"
                  nextIcon={undefined}
                />
              ) : (
                <DefaultBtn
                  label={t('block')}
                  onClick={handleShowBlockView}
                  disabled={isProcessing}
                  className="font-csc65 w-full"
                  style={{ backgroundColor: '#EB7C0C' }}
                  color="white"
                  nextIcon={undefined}
                />
              )}
            </div>
          </>
        )}
      </Container>
    </>
  )
}

EventManager.whyDidYouRender = true

export default memo(EventManager)
